'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RealtimeConfig } from '../types/app-config';
import { useRuntimeAuth } from '../auth/auth-context';

export interface RealtimeEvent {
  channel?: string;
  type?: string;
  event?: string;
  action?: string;
  entity?: string;
  data?: unknown;
  payload?: unknown;
  [key: string]: unknown;
}

export interface PresenceUser {
  id: string;
  name: string;
  avatarUrl?: string;
  status?: 'online' | 'offline' | 'away' | 'busy';
}

export interface RealtimeContextValue {
  isConnected: boolean;
  subscribe: (
    channel: string,
    callback: (event: RealtimeEvent) => void,
  ) => () => void;
  presenceUsers: PresenceUser[];
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

const DEFAULT_RECONNECT = {
  enabled: true,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  factor: 2,
  maxAttempts: Infinity,
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function asString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizePresenceUser(user: unknown): PresenceUser | null {
  const record = asRecord(user);
  if (!record) return null;

  const id = asString(record.id) || asString(record.userId) || asString(record.user_id);
  if (!id) return null;

  const name = asString(record.name)
    || asString(record.displayName)
    || asString(record.display_name)
    || asString(record.email)
    || id;
  const avatarUrl = asString(record.avatarUrl) || asString(record.avatar_url) || undefined;
  const statusValue = asString(record.status);
  const status = statusValue === 'online'
    || statusValue === 'offline'
    || statusValue === 'away'
    || statusValue === 'busy'
    ? statusValue
    : undefined;

  return {
    id,
    name,
    avatarUrl,
    status,
  };
}

function extractPresenceList(event: RealtimeEvent): PresenceUser[] | null {
  const candidates: unknown[] = [];

  candidates.push((event as Record<string, unknown>).activeUsers);
  candidates.push((event as Record<string, unknown>).users);

  const dataRecord = asRecord(event.data);
  if (dataRecord) {
    candidates.push(dataRecord.activeUsers);
    candidates.push(dataRecord.users);
  }

  const payloadRecord = asRecord(event.payload);
  if (payloadRecord) {
    candidates.push(payloadRecord.activeUsers);
    candidates.push(payloadRecord.users);
  }

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const normalized = candidate
      .map(normalizePresenceUser)
      .filter((user): user is PresenceUser => user !== null);
    return normalized;
  }

  return null;
}

function extractPresenceUser(event: RealtimeEvent): PresenceUser | null {
  const candidateValues: unknown[] = [];
  candidateValues.push((event as Record<string, unknown>).user);
  const dataRecord = asRecord(event.data);
  if (dataRecord) {
    candidateValues.push(dataRecord.user);
  }
  const payloadRecord = asRecord(event.payload);
  if (payloadRecord) {
    candidateValues.push(payloadRecord.user);
  }

  for (const candidate of candidateValues) {
    const normalized = normalizePresenceUser(candidate);
    if (normalized) return normalized;
  }

  return null;
}

function looksLikePresenceEvent(
  event: RealtimeEvent,
  presenceChannel: string,
): boolean {
  const channel = asString(event.channel);
  const type = asString(event.type);
  const eventName = asString(event.event);

  if (channel && channel === presenceChannel) return true;
  if (type && type.startsWith('presence')) return true;
  if (eventName && eventName.startsWith('presence')) return true;
  return false;
}

function parseRealtimeMessage(raw: string): RealtimeEvent | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    const record = asRecord(parsed);
    return record ? record as RealtimeEvent : null;
  } catch {
    return null;
  }
}

export interface RealtimeProviderProps {
  children: ReactNode;
  config?: RealtimeConfig;
}

export function RealtimeProvider({ children, config }: RealtimeProviderProps) {
  const { token } = useRuntimeAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const subscriptionsRef = useRef(
    new Map<string, Set<(event: RealtimeEvent) => void>>(),
  );

  const presenceChannel = config?.presenceChannel || 'presence';
  const reconnect = {
    ...DEFAULT_RECONNECT,
    ...config?.reconnectStrategy,
  };

  const sendMessage = useCallback((message: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(message));
  }, []);

  const dispatchEvent = useCallback((channel: string, event: RealtimeEvent) => {
    const targetSet = subscriptionsRef.current.get(channel);
    if (targetSet) {
      targetSet.forEach(callback => callback(event));
    }

    const wildcardSet = subscriptionsRef.current.get('*');
    if (wildcardSet) {
      wildcardSet.forEach(callback => callback(event));
    }
  }, []);

  const handlePresenceEvent = useCallback((event: RealtimeEvent) => {
    const snapshot = extractPresenceList(event);
    if (snapshot) {
      setPresenceUsers(snapshot);
      return;
    }

    const eventName = asString(event.event) || asString(event.type) || '';
    const user = extractPresenceUser(event);
    if (!user) return;

    if (eventName.includes('leave')) {
      setPresenceUsers(prev => prev.filter(item => item.id !== user.id));
      return;
    }

    setPresenceUsers(prev => {
      const existingIndex = prev.findIndex(item => item.id === user.id);
      if (existingIndex === -1) return [...prev, user];

      const next = [...prev];
      next[existingIndex] = user;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!config?.wsUrl) {
      setIsConnected(false);
      setPresenceUsers([]);
      return;
    }

    let closedByEffectCleanup = false;

    const connect = () => {
      const socket = new WebSocket(config.wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        if (token) {
          sendMessage({ type: 'auth', token });
        }

        subscriptionsRef.current.forEach((_callbacks, channel) => {
          sendMessage({ type: 'subscribe', channel });
        });
      };

      socket.onmessage = (messageEvent: MessageEvent<string>) => {
        const event = parseRealtimeMessage(messageEvent.data);
        if (!event) return;

        const channel = asString(event.channel);
        if (channel) {
          dispatchEvent(channel, event);
        } else if (asString(event.entity)) {
          dispatchEvent(`entity:${asString(event.entity)}`, event);
        }

        if (looksLikePresenceEvent(event, presenceChannel)) {
          handlePresenceEvent(event);
        }
      };

      socket.onclose = () => {
        setIsConnected(false);
        socketRef.current = null;

        if (closedByEffectCleanup) return;
        if (!reconnect.enabled) return;
        if (reconnectAttemptsRef.current >= reconnect.maxAttempts) return;

        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(
          reconnect.maxDelayMs,
          reconnect.initialDelayMs * (reconnect.factor ** attempt),
        );
        reconnectAttemptsRef.current += 1;

        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByEffectCleanup = true;

      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
      }

      const socket = socketRef.current;
      if (socket) {
        socket.close();
      }
      socketRef.current = null;
      setIsConnected(false);
      setPresenceUsers([]);
    };
  }, [
    config?.wsUrl,
    reconnect.enabled,
    reconnect.factor,
    reconnect.initialDelayMs,
    reconnect.maxAttempts,
    reconnect.maxDelayMs,
    token,
    dispatchEvent,
    handlePresenceEvent,
    presenceChannel,
    sendMessage,
  ]);

  const subscribe = useCallback((
    channel: string,
    callback: (event: RealtimeEvent) => void,
  ) => {
    if (!channel) {
      return () => undefined;
    }

    const existingSet = subscriptionsRef.current.get(channel);
    if (existingSet) {
      existingSet.add(callback);
    } else {
      subscriptionsRef.current.set(channel, new Set([callback]));
      sendMessage({ type: 'subscribe', channel });
    }

    return () => {
      const callbacks = subscriptionsRef.current.get(channel);
      if (!callbacks) return;

      callbacks.delete(callback);
      if (callbacks.size > 0) return;

      subscriptionsRef.current.delete(channel);
      sendMessage({ type: 'unsubscribe', channel });
    };
  }, [sendMessage]);

  const value = useMemo<RealtimeContextValue>(() => ({
    isConnected,
    subscribe,
    presenceUsers,
  }), [isConnected, subscribe, presenceUsers]);

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
}

export function useRealtimeSubscription(
  channel: string | undefined,
  callback: (event: RealtimeEvent) => void,
) {
  const { subscribe } = useRealtime();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!channel) return undefined;
    return subscribe(channel, event => callbackRef.current(event));
  }, [channel, subscribe]);
}

export function usePresence(): PresenceUser[] {
  const { presenceUsers } = useRealtime();
  return presenceUsers;
}

export function isMutationRealtimeEvent(event: RealtimeEvent): boolean {
  const type = asString(event.type);
  const eventName = asString(event.event);
  const action = asString(event.action);

  const candidates = [type, eventName, action]
    .filter((value): value is string => !!value)
    .map(value => value.toLowerCase());

  if (candidates.length === 0) return true;

  return candidates.some(value => (
    value.includes('mutation')
    || value.includes('create')
    || value.includes('update')
    || value.includes('delete')
  ));
}
